import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Key as KeyIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  _MenuItemButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { getOrganization, getProfile } from 'client/modules/Auth/AuthReducer';
import {
  clearPasskeyError,
  deletePasskey,
  listPasskeys,
  registerPasskey,
  renamePasskey,
} from 'client/modules/Auth/PasskeyActions';

const messages = defineMessages({
  title: {
    id: 'Passkey.Title',
    defaultMessage: 'Passkeys',
  },
  description: {
    id: 'Passkey.Description',
    defaultMessage:
      'Passkeys provide a more secure and convenient way to sign in without passwords.',
  },
  addPasskey: {
    id: 'Passkey.Add',
    defaultMessage: 'Add Passkey',
  },
  noPasskeys: {
    id: 'Passkey.NoPasskeys',
    defaultMessage: 'No passkeys registered',
  },
  noPasskeysDescription: {
    id: 'Passkey.NoPasskeysDescription',
    defaultMessage: 'Add a passkey to enable secure, passwordless authentication.',
  },
  addPasskeyTitle: {
    id: 'Passkey.AddTitle',
    defaultMessage: 'Add Passkey',
  },
  addPasskeyDescription: {
    id: 'Passkey.AddDescription',
    defaultMessage: 'Choose a name for your passkey and follow the prompts to register it.',
  },
  passkeyName: {
    id: 'Passkey.Name',
    defaultMessage: 'Passkey Name',
  },
  passkeyNamePlaceholder: {
    id: 'Passkey.NamePlaceholder',
    defaultMessage: 'e.g., iPhone, MacBook, YubiKey',
  },
  rename: {
    id: 'Passkey.Rename',
    defaultMessage: 'Rename',
  },
  delete: {
    id: 'Passkey.Delete',
    defaultMessage: 'Delete',
  },
  renameTitle: {
    id: 'Passkey.RenameTitle',
    defaultMessage: 'Rename Passkey',
  },
  deleteTitle: {
    id: 'Passkey.DeleteTitle',
    defaultMessage: 'Delete Passkey',
  },
  deleteConfirmation: {
    id: 'Passkey.DeleteConfirmation',
    defaultMessage: 'Are you sure you want to delete this passkey? This action cannot be undone.',
  },
  addSuccess: {
    id: 'Passkey.AddSuccess',
    defaultMessage: 'Passkey added successfully',
  },
  renameSuccess: {
    id: 'Passkey.RenameSuccess',
    defaultMessage: 'Passkey renamed successfully',
  },
  deleteSuccess: {
    id: 'Passkey.DeleteSuccess',
    defaultMessage: 'Passkey deleted successfully',
  },
  createdAt: {
    id: 'Passkey.CreatedAt',
    defaultMessage: 'Created {date}',
  },
  lastUsed: {
    id: 'Passkey.LastUsed',
    defaultMessage: 'Last used {date}',
  },
  neverUsed: {
    id: 'Passkey.NeverUsed',
    defaultMessage: 'Never used',
  },
  cancel: {
    id: 'Common.Cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 'Common.Save',
    defaultMessage: 'Save',
  },
  register: {
    id: 'Passkey.Register',
    defaultMessage: 'Register',
  },
  enforcedWarning: {
    id: 'TwoFactor.EnforcedWarning',
    defaultMessage:
      'Two-factor authentication is enforced by your organization. You cannot disable it.',
  },
});

// Helper function to get device name
const getDeviceName = () => {
  const { userAgent } = navigator;
  const { platform } = navigator;

  // Mobile devices
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent)) return 'iPad';
  if (/Android/i.test(userAgent)) {
    // Try to extract device name from Android user agent
    const match = userAgent.match(/Android.*?;.*?([^;]+)\)/);
    return match && match[1] ? match[1].trim() : 'Android Device';
  }

  // Desktop platforms
  if (/Mac/i.test(platform)) {
    // Try to determine if it's a MacBook, iMac, etc.
    if (/MacIntel/i.test(platform)) return 'Mac';
    return 'Mac';
  }
  if (/Win/i.test(platform)) return 'Windows PC';
  if (/Linux/i.test(platform)) return 'Linux Device';

  // Browser-specific fallbacks
  if (/Chrome/i.test(userAgent)) return 'Chrome Browser';
  if (/Firefox/i.test(userAgent)) return 'Firefox Browser';
  if (/Safari/i.test(userAgent)) return 'Safari Browser';
  if (/Edge/i.test(userAgent)) return 'Edge Browser';

  // Final fallback
  return 'This Device';
};

function PasskeySettings({ intl }) {
  const { formatMessage, formatDate } = intl;
  const dispatch = useDispatch();

  const { passkeys, isLoading, error } = useSelector(state => state.passkeys);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPasskey, setSelectedPasskey] = useState(null);
  const [passkeyName, setPasskeyName] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const organization = useSelector(getOrganization);
  const profile = useSelector(getProfile);

  const disableDelete = organization?.twoFactor?.enforced && !profile.hasTwoFactorAuth;

  useEffect(() => {
    dispatch(listPasskeys());
  }, [dispatch]);

  const handleAddOpen = useCallback(() => {
    setPasskeyName(getDeviceName());
    setAddDialogOpen(true);
    dispatch(clearPasskeyError());
  }, [dispatch]);

  const handleAddClose = useCallback(() => {
    setAddDialogOpen(false);
    setPasskeyName('');
  }, []);

  const handleAddPasskey = useCallback(async () => {
    try {
      await dispatch(registerPasskey(passkeyName || getDeviceName()));
      handleAddClose();
    } catch (_err) {
      // Error is handled by reducer
    }
  }, [dispatch, passkeyName, handleAddClose]);

  const handleMenuOpen = useCallback((event, passkey) => {
    // Validate passkey structure
    if (!passkey || !passkey.id) {
      console.error('Invalid passkey object:', passkey);
      return;
    }

    setAnchorEl(event.currentTarget);
    setSelectedPasskey(passkey);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedPasskey(null);
  }, []);

  const handleRenameOpen = useCallback(() => {
    setPasskeyName(selectedPasskey?.name || '');
    setRenameDialogOpen(true);
    // Don't call handleMenuClose here - we need to keep selectedPasskey
    setAnchorEl(null); // Just close the menu
    dispatch(clearPasskeyError());
  }, [selectedPasskey, dispatch]);

  const handleRenameClose = useCallback(() => {
    setRenameDialogOpen(false);
    setPasskeyName('');
    setSelectedPasskey(null); // Clear selectedPasskey only when closing
  }, []);

  const handleRenamePasskey = useCallback(async () => {
    try {
      if (!selectedPasskey) {
        console.error('No passkey selected for rename');
        return;
      }
      await dispatch(renamePasskey(selectedPasskey.id, passkeyName));
      handleRenameClose();
      // Refresh the passkey list to ensure UI is updated
      dispatch(listPasskeys());
    } catch (err) {
      // Error is handled by reducer, but we should log it for debugging
      console.error('Failed to rename passkey:', err);
      // Don't close the dialog on error so user can see the error message
    }
  }, [dispatch, selectedPasskey, passkeyName, handleRenameClose]);

  const handleDeleteOpen = useCallback(() => {
    setDeleteDialogOpen(true);
    // Don't call handleMenuClose here - we need to keep selectedPasskey
    setAnchorEl(null); // Just close the menu
    dispatch(clearPasskeyError());
  }, [dispatch]);

  const handleDeleteClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedPasskey(null); // Clear selectedPasskey only when closing
  }, []);

  const handleDeletePasskey = useCallback(async () => {
    try {
      if (!selectedPasskey) {
        console.error('No passkey selected for deletion');
        return;
      }
      await dispatch(deletePasskey(selectedPasskey.id));
      handleDeleteClose();
      // Refresh the passkey list to ensure UI is updated
      dispatch(listPasskeys());
    } catch (err) {
      // Error is handled by reducer, but we should log it for debugging
      console.error('Failed to delete passkey:', err);
      // Don't close the dialog on error so user can see the error message
    }
  }, [dispatch, selectedPasskey, handleDeleteClose]);

  const formatPasskeyDate = useCallback(
    dateString => {
      if (!dateString) return formatMessage(messages.neverUsed);
      return formatDate(new Date(dateString), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    [formatDate, formatMessage],
  );

  if (!organization) {
    return <Skeleton variant="rounded" width="100%" height={300} />;
  }

  return (
    <Card elevation={1}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6">{formatMessage(messages.title)}</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {formatMessage(messages.description)}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {passkeys.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <KeyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {formatMessage(messages.noPasskeys)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {formatMessage(messages.noPasskeysDescription)}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddOpen}
              disabled={isLoading}
            >
              {formatMessage(messages.addPasskey)}
            </Button>
          </Box>
        ) : (
          <>
            <List>
              {passkeys.map(passkey => {
                return (
                  <ListItem key={passkey.id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{passkey.name}</Typography>
                          {passkey.transports && passkey.transports.length > 0 && (
                            <Chip
                              size="small"
                              label={passkey.transports.join(', ')}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatMessage(messages.createdAt, {
                              date: formatPasskeyDate(passkey.createdAt),
                            })}
                          </Typography>
                          {passkey.lastUsedAt && (
                            <Typography variant="body2" color="text.secondary">
                              {formatMessage(messages.lastUsed, {
                                date: formatPasskeyDate(passkey.lastUsedAt),
                              })}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={e => {
                          handleMenuOpen(e, passkey);
                        }}
                        disabled={isLoading}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddOpen}
                disabled={isLoading}
              >
                {formatMessage(messages.addPasskey)}
              </Button>
            </Box>
          </>
        )}

        {/* Context Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleRenameOpen}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            {formatMessage(messages.rename)}
          </MenuItem>
          <Tooltip title={disableDelete ? formatMessage(messages.enforcedWarning) : ''}>
            <span>
              <MenuItem
                onClick={handleDeleteOpen}
                sx={{ color: 'error.main' }}
                disabled={disableDelete}
              >
                <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                {formatMessage(messages.delete)}
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>

        {/* Add Passkey Dialog */}
        <Dialog open={addDialogOpen} onClose={handleAddClose} maxWidth="sm" fullWidth>
          <DialogTitle>{formatMessage(messages.addPasskeyTitle)}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {formatMessage(messages.addPasskeyDescription)}
            </Typography>
            <TextField
              fullWidth
              label={formatMessage(messages.passkeyName)}
              placeholder={formatMessage(messages.passkeyNamePlaceholder)}
              value={passkeyName}
              onChange={e => setPasskeyName(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddClose} disabled={isLoading}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              onClick={handleAddPasskey}
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : null}
            >
              {formatMessage(messages.register)}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rename Dialog */}
        <Dialog open={renameDialogOpen} onClose={handleRenameClose} maxWidth="sm" fullWidth>
          <DialogTitle>{formatMessage(messages.renameTitle)}</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              label={formatMessage(messages.passkeyName)}
              value={passkeyName}
              onChange={e => setPasskeyName(e.target.value)}
              disabled={isLoading}
              autoFocus
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRenameClose} disabled={isLoading}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              onClick={handleRenamePasskey}
              variant="contained"
              disabled={isLoading || !passkeyName.trim()}
              startIcon={isLoading ? <CircularProgress size={16} /> : null}
            >
              {formatMessage(messages.save)}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteClose} maxWidth="sm" fullWidth>
          <DialogTitle>{formatMessage(messages.deleteTitle)}</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Typography>{formatMessage(messages.deleteConfirmation)}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClose} disabled={isLoading}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              onClick={handleDeletePasskey}
              color="error"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : null}
            >
              {formatMessage(messages.delete)}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default injectIntl(PasskeySettings);
